import React from 'react';
import { useDashboard } from '../DashboardContext';

interface Props { compareMode: boolean; }

// 12 cells total: 4-column × 3-row grid per spec
// Cell 12 = Event Attendees (per UAS-2291 relationship note)
// TEMP: emailOpens + whatsappReads hidden from the grid (kept in CELLS for easy restore)
const HIDDEN_CELL_KEYS = new Set(['emailOpens', 'whatsappReads']);

const CELLS = [
    { key: 'platformLogins',    icon: '🔑', label: 'Platform Logins',    color: '#3b82f6' },
    { key: 'emailOpens',        icon: '📧', label: 'Email Opens',         color: '#0ea5e9' },
    { key: 'whatsappReads',     icon: '💬', label: 'WhatsApp Reads',      color: '#22c55e' },
    { key: 'profileViews',      icon: '👁️', label: 'Profile Views',       color: '#8b5cf6' },
    { key: 'messagingActions',  icon: '💌', label: 'Messages Sent',       color: '#f59e0b' },
    { key: 'uniqueActiveUsers', icon: '👥', label: 'Unique Active Users', color: '#06b6d4' },
    { key: 'guidanceVideos',    icon: '🎥', label: 'Guidance Videos',     color: '#ef4444' },
    { key: 'photoAlbums',       icon: '📸', label: 'Photo Albums',        color: '#ec4899' },
    { key: 'qnaPosts',          icon: '💬', label: 'Q&A Posts',           color: '#10b981' },
    { key: 'jobPostings',       icon: '💼', label: 'Job Postings',        color: '#6366f1' },
    { key: 'otherEngagement',   icon: '📞', label: 'Other Engagement',    color: '#64748b' },
    { key: 'eventAttendees',    icon: '📅', label: 'Event Attendees',     color: '#f97316' }, // cell 12
];

// Cells that should span a wider column
const WIDE_CELLS = new Set(['platformLogins', 'profileViews']);

const OtherActivityGrid: React.FC<Props> = ({ compareMode }) => {
    const { otherActions, eventActivity, loading } = useDashboard();

    // Event Attendees: from eventActivity (always present in grid, 0 if no events)
    const eventAttendees = eventActivity?.attended ?? 0;

    return (
        <div className="mb-0">
            <h5 id="section-other-actions" className="font-16 font-600 mb-3">⚡ Other Actions</h5>
            <div className="card">
                <div className="card-body p-0">
                    {/* 4-column × 3-row grid (12 cells) per spec */}
                    <div className="row g-0">
                        {CELLS.filter((cell) => !HIDDEN_CELL_KEYS.has(cell.key)).map((cell) => {
                            const value = cell.key === 'eventAttendees'
                                ? eventAttendees
                                : (otherActions?.[cell.key] ?? 0);

                            const colClasses = WIDE_CELLS.has(cell.key)
                                ? 'col-6 col-md-6 col-lg-6 p-3 border-end border-bottom'
                                : 'col-6 col-md-6 col-lg-3 p-3 border-end border-bottom';


                            return (
                                <div key={cell.key}
                                    className={colClasses}>
                                    {/* Icon — 32px coloured rounded square */}
                                    <div className="rounded d-flex align-items-center justify-content-center mb-2"
                                        style={{
                                            background: cell.color + '20',
                                            width: 32, height: 32,
                                            fontSize: 16,
                                        }}>
                                        {cell.icon}
                                    </div>

                                    {loading ? (
                                        <div className="placeholder-glow">
                                            <span className="placeholder col-8 d-block mb-1" style={{ height: 22 }}></span>
                                            <span className="placeholder col-10 d-block" style={{ height: 10 }}></span>
                                        </div>
                                    ) : (
                                        <>
                                            {/* Metric value — 18px bold */}
                                            <div className="fw-bold" style={{ fontSize: 18 }}>
                                                {cell.key === 'eventAttendees' && value === 0
                                                    ? <span className="text-muted" style={{ fontSize: 12 }}>No events</span>
                                                    : value.toLocaleString()
                                                }
                                            </div>
                                            {/* Label — 10px muted all caps */}
                                            <div className="text-uppercase text-muted" style={{ fontSize: 10 }}>
                                                {cell.label}
                                            </div>
                                            {/* Current period delta — grey = placeholder (no prior period data yet) */}
                                            <div className="mt-1" style={{ fontSize: 10, color: '#94a3b8' }}>
                                                = &nbsp;vs prev period
                                            </div>
                                            {/* Compare row — visible only in Compare mode */}
                                            {compareMode && (
                                                <div className="text-muted mt-1" style={{ fontSize: 10 }}>
                                                    Prev: —
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OtherActivityGrid;
